import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AIMatchEstimator from '../components/AIMatchEstimator';
import { SEO_CONFIGS } from "../utils/seoHelper";
import SEOHead from "../components/SEOHead";
import './Home.css';

const CITIES = ['Raipur', 'Bhilai', 'Durg', 'Rajnandgaon', 'Bilaspur', 'Korba'];
const EVENTS = ['Shaadi', 'Birthday', 'Engagement', 'Rice Ceremony', 'Griha Pravesh', 'Corporate', 'Baby Shower', 'Anniversary'];

const STATS = [
  { icon: '🎊', count: '200+', label: 'Decorators' },
  { icon: '💒', count: '1500+', label: 'Events Done' },
  { icon: '📍', count: '6', label: 'Cities' },
  { icon: '⭐', count: '4.8', label: 'Avg Rating' },
];

const EVENTS_DATA = [
  { name: 'Shaadi', emoji: '💒', desc: 'Wedding decoration', gradient: 'linear-gradient(135deg,#ff6b6b,#ee5a24)' },
  { name: 'Birthday', emoji: '🎂', desc: 'Birthday parties', gradient: 'linear-gradient(135deg,#a29bfe,#6c5ce7)' },
  { name: 'Engagement', emoji: '💍', desc: 'Sagai ceremony', gradient: 'linear-gradient(135deg,#fd79a8,#e84393)' },
  { name: 'Rice Ceremony', emoji: '🍚', desc: 'Annaprashan', gradient: 'linear-gradient(135deg,#55efc4,#00b894)' },
  { name: 'Griha Pravesh', emoji: '🏠', desc: 'Naye ghar ki khushi', gradient: 'linear-gradient(135deg,#ffeaa7,#fdcb6e)' },
  { name: 'Corporate', emoji: '💼', desc: 'Office events', gradient: 'linear-gradient(135deg,#74b9ff,#0984e3)' },
  { name: 'Baby Shower', emoji: '👶', desc: 'Godh bharai', gradient: 'linear-gradient(135deg,#fab1a0,#e17055)' },
  { name: 'Anniversary', emoji: '❤️', desc: 'Saalgirah', gradient: 'linear-gradient(135deg,#ff7675,#d63031)' },
];

const TESTIMONIALS = [
  { name: 'Priya Sharma', city: 'Raipur', event: 'Shaadi', text: 'Ekdum amazing decoration thi! Sab mehman tarif karte rahe. 100% recommend karungi.', rating: 5, avatar: '👩' },
  { name: 'Rahul Verma', city: 'Bhilai', event: 'Birthday', text: 'Budget mein best decoration mili. Decorator bahut professional tha aur time pe aaya.', rating: 5, avatar: '👨' },
  { name: 'Sunita Patel', city: 'Durg', event: 'Engagement', text: 'DecorHub se booking bahut aasaan raha. Photos dekh ke hi trust ho gaya, bilkul waise hi bana.', rating: 5, avatar: '👩‍🦱' },
  { name: 'Amit Sahu', city: 'Bilaspur', event: 'Griha Pravesh', text: 'Ghar ki decoration dekhke aankhein bhar aayi. Bhagwan ka shukr aur DecorHub ka shukriya!', rating: 5, avatar: '🧔' },
];

// Animated counter hook
function useCounter(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isDecimal = String(target).includes('.');
    const numericTarget = parseFloat(target);
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * numericTarget;
      setCount(isDecimal ? current.toFixed(1) : Math.floor(current));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

const StatCard = ({ icon, count, label, animate }) => {
  const numeric = parseFloat(count);
  const suffix = count.replace(/[\d.]/g, '');
  const animated = useCounter(numeric, 1800, animate);
  return (
    <div className="dh-stat-card">
      <span className="dh-stat-icon">{icon}</span>
      <span className="dh-stat-count">{animated}{suffix}</span>
      <span className="dh-stat-label">{label}</span>
    </div>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const [city, setCity] = useState('');
  const [event, setEvent] = useState('');
  const [statsVisible, setStatsVisible] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const statsRef = useRef(null);
  const heroRef = useRef(null);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    if (event) params.set('service', event);
    navigate(`/decorators?${params.toString()}`);
  };

  // Parallax scroll
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Stats intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % TESTIMONIALS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (

    
    <div className="dh-home">
<SEOHead {...SEO_CONFIGS.home} />
      {/* ═══════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════ */}
      <section className="dh-hero" ref={heroRef}>
        {/* Animated background orbs */}
        <div className="dh-hero-bg">
          <div className="dh-orb dh-orb-1" style={{ transform: `translateY(${scrollY * 0.15}px)` }} />
          <div className="dh-orb dh-orb-2" style={{ transform: `translateY(${scrollY * 0.08}px)` }} />
          <div className="dh-orb dh-orb-3" style={{ transform: `translateY(${scrollY * 0.12}px)` }} />
          <div className="dh-hero-grid" />
        </div>

        <div className="dh-container dh-hero-content">
          {/* Badge */}
          <div className="dh-hero-badge">
            <span className="dh-badge-dot" />
            🎉 India Ka #1 Event Finding Platform
          </div>

          {/* Headline */}
          <h1 className="dh-hero-title">
            Apne Events Ko
            <br />
            <span className="dh-hero-highlight">
              Unforgettable
              <svg className="dh-underline-svg" viewBox="0 0 300 12" preserveAspectRatio="none">
                <path d="M0,10 Q75,0 150,8 Q225,16 300,6" stroke="url(#gold-grad)" strokeWidth="3" fill="none" strokeLinecap="round" />
                <defs>
                  <linearGradient id="gold-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#C8860A" />
                    <stop offset="100%" stopColor="#f59e0b" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            {' '}Banao
          </h1>

          <p className="dh-hero-sub">
            Raipur, Delhi, Mumbai ke best decorators ek jagah —<br className="dh-br-hide" />
            <strong>trusted, verified, affordable</strong>
          </p>

       

          {/* Search Box */}
          <div className="dh-search-box">
            <div className="dh-search-field">
              <span className="dh-search-icon">📍</span>
              <select value={city} onChange={e => setCity(e.target.value)}>
                <option value="">City Chuno</option>
                {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="dh-search-divider" />
            <div className="dh-search-field">
              <span className="dh-search-icon">🎊</span>
              <select value={event} onChange={e => setEvent(e.target.value)}>
                <option value="">Event Type</option>
                {EVENTS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <button className="dh-search-btn" onClick={handleSearch}>
              <span>Decorator Dhundho</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
            </button>
          </div>

          {/* Trust pills */}
          <div className="dh-trust-pills">
            {['✅ Free Booking', '🔒 Verified Decorators', '💰 Best Price', '⚡ Quick Response'].map(t => (
              <span key={t} className="dh-trust-pill">{t}</span>
            ))}
          </div>
        </div>

        {/* Floating decoration elements */}
        <div className="dh-float-1">🎀</div>
        <div className="dh-float-2">✨</div>
        <div className="dh-float-3">🌸</div>
        <div className="dh-float-4">🎊</div>

        {/* Scroll indicator */}
        <div className="dh-scroll-indicator">
          <div className="dh-scroll-dot" />
        </div>
      </section>

             <AIMatchEstimator />

      {/* ═══════════════════════════════════
          STATS SECTION
      ═══════════════════════════════════ */}
      <section className="dh-stats-section" ref={statsRef}>
        <div className="dh-container">
          <div className="dh-stats-grid">
            {STATS.map(s => (
              <StatCard key={s.label} {...s} animate={statsVisible} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════
          EVENT TYPES
      ═══════════════════════════════════ */}
      <section className="dh-section dh-events-section">
        <div className="dh-container">
          <div className="dh-section-header">
            <span className="dh-section-tag">Events</span>
            <h2 className="dh-section-title">Kaunse Events Ke Liye?</h2>
            <p className="dh-section-sub">Chhote se chhota aur bade se bada — sab milega</p>
          </div>

          <div className="dh-events-grid">
            {EVENTS_DATA.map((ev, i) => (
              <div
                key={ev.name}
                className="dh-event-card"
                style={{ animationDelay: `${i * 0.06}s` }}
                onClick={() => navigate(`/decorators?service=${ev.name}`)}
              >
                <div className="dh-event-glow" style={{ background: ev.gradient }} />
                <div className="dh-event-emoji">{ev.emoji}</div>
                <div className="dh-event-name">{ev.name}</div>
                <div className="dh-event-desc">{ev.desc}</div>
                <div className="dh-event-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════
          HOW IT WORKS
      ═══════════════════════════════════ */}
      <section className="dh-section dh-how-section">
        <div className="dh-container">
          <div className="dh-section-header">
            <span className="dh-section-tag">Process</span>
            <h2 className="dh-section-title">Kaise Kaam Karta Hai?</h2>
            <p className="dh-section-sub">Sirf 3 steps mein booking ho jaati hai</p>
          </div>

          <div className="dh-steps-wrapper">
            {/* Connector line */}
            <div className="dh-steps-line" />

            <div className="dh-steps-grid">
              {[
                { step: '01', icon: '🔍', title: 'Decorator Dhundho', desc: 'City aur event type ke hisaab se best decorators dekho, portfolio check karo, ratings padho', color: '#C8860A' },
                { step: '02', icon: '📅', title: 'Book Karo', desc: 'Date, venue, budget select karo aur booking confirm karo — bilkul free mein', color: '#7c3aed' },
                { step: '03', icon: '🎊', title: 'Enjoy Karo', desc: 'Decorator apna kaam karega aur tumhara event yadgaar ban jaayega', color: '#10b981' },
              ].map((item, i) => (
                <div key={item.step} className="dh-step-card" style={{ '--step-color': item.color }}>
                  <div className="dh-step-number" style={{ background: item.color }}>{item.step}</div>
                  <div className="dh-step-icon-wrap" style={{ borderColor: item.color + '40' }}>
                    <span className="dh-step-icon">{item.icon}</span>
                  </div>
                  <h3 className="dh-step-title">{item.title}</h3>
                  <p className="dh-step-desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════
          TESTIMONIALS
      ═══════════════════════════════════ */}
      <section className="dh-section dh-testimonials-section">
        <div className="dh-container">
          <div className="dh-section-header">
            <span className="dh-section-tag">Reviews</span>
            <h2 className="dh-section-title">Customers Kya Kehte Hain?</h2>
            <p className="dh-section-sub">1500+ happy customers ki real stories</p>
          </div>

          <div className="dh-testimonials-wrapper">
            {/* Active card */}
            <div className="dh-testimonial-main">
              <div className="dh-testimonial-quote">"</div>
              <p className="dh-testimonial-text">{TESTIMONIALS[activeTestimonial].text}</p>
              <div className="dh-testimonial-stars">
                {'⭐'.repeat(TESTIMONIALS[activeTestimonial].rating)}
              </div>
              <div className="dh-testimonial-author">
                <span className="dh-testimonial-avatar">{TESTIMONIALS[activeTestimonial].avatar}</span>
                <div>
                  <div className="dh-testimonial-name">{TESTIMONIALS[activeTestimonial].name}</div>
                  <div className="dh-testimonial-meta">{TESTIMONIALS[activeTestimonial].city} · {TESTIMONIALS[activeTestimonial].event}</div>
                </div>
              </div>
            </div>

            {/* Dots */}
            <div className="dh-testimonial-dots">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  className={`dh-dot ${i === activeTestimonial ? 'active' : ''}`}
                  onClick={() => setActiveTestimonial(i)}
                />
              ))}
            </div>

            {/* Side cards */}
            <div className="dh-testimonial-side">
              {TESTIMONIALS.filter((_, i) => i !== activeTestimonial).slice(0, 2).map((t, i) => (
                <div key={i} className="dh-testimonial-mini" onClick={() => setActiveTestimonial(TESTIMONIALS.indexOf(t))}>
                  <span className="dh-testimonial-avatar">{t.avatar}</span>
                  <div>
                    <div className="dh-testimonial-name">{t.name}</div>
                    <div className="dh-testimonial-mini-text">"{t.text.slice(0, 60)}..."</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════
          CITIES
      ═══════════════════════════════════ */}
      <section className="dh-section dh-cities-section">
        <div className="dh-container">
          <div className="dh-section-header">
            <span className="dh-section-tag">Cities</span>
            <h2 className="dh-section-title">Apni City Chuno</h2>
          </div>
          <div className="dh-cities-grid">
            {CITIES.map((c, i) => (
              <div key={c} className="dh-city-card" onClick={() => navigate(`/decorators?city=${c}`)}>
                <div className="dh-city-bg" />
                <span className="dh-city-icon">📍</span>
                <span className="dh-city-name">{c}</span>
                <span className="dh-city-arrow">→</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════
          CTA SECTION
      ═══════════════════════════════════ */}
      <section className="dh-cta-section">
        <div className="dh-cta-bg">
          <div className="dh-cta-orb-1" />
          <div className="dh-cta-orb-2" />
        </div>
        <div className="dh-container dh-cta-content">
          <div className="dh-cta-badge">🎨 Decorators ke liye</div>
          <h2 className="dh-cta-title">Kya Aap Decorator Hain?</h2>
          <p className="dh-cta-sub">Free mein register karo aur hazaro clients tak pahuncho.<br />No commission on registration. Sirf results.</p>

          <div className="dh-cta-stats">
            {[
              { val: '200+', label: 'Active Decorators' },
              { val: '1500+', label: 'Bookings Done' },
              { val: '₹0', label: 'Registration Fee' },
            ].map(s => (
              <div key={s.label} className="dh-cta-stat">
                <div className="dh-cta-stat-val">{s.val}</div>
                <div className="dh-cta-stat-label">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="dh-cta-btns">
            <a href="/register" className="dh-cta-btn-primary">
              🚀 Abhi Register Karo — Free
            </a>
            <a href="/decorators" className="dh-cta-btn-outline">
              Decorators Dekho →
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}